import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Html, Edges, Text, Bounds } from "@react-three/drei";
import * as THREE from "three";
import { fetchUserQuestions } from "../../functions/fetchUserQuestions";
import { selectAdminToken } from "../../admin/sessionSlice";
import Swal from "sweetalert2";
import { updateScore } from "../../functions/updateScore";
import { sendReport } from "../../functions/sendReport";

import {
  Timestamp,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { database } from "../../database/firebase";
import "./JengaGame.css";

const OPTION_COLORS = {
  A: "#9B4DFF",
  B: "#F24B6D",
  C: "#F5C84B",
  D: "#e85d75",  // Pink/magenta
};

const WOOD_COLOR = "#E8D5A3";
const LABELS = ["A", "B", "C"];
const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const MAX_LIVES = 5;

const COIN_IMG_SRC =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <defs><linearGradient id="gc" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#ffe566"/><stop offset="45%" stop-color="#f5b800"/>
        <stop offset="100%" stop-color="#c98700"/>
      </linearGradient></defs>
      <circle cx="20" cy="20" r="17" fill="url(#gc)" stroke="#9a6b0a" stroke-width="1.4"/>
      <ellipse cx="16" cy="15" rx="7" ry="4" fill="rgba(255,255,255,0.35)"/>
      <text x="20" y="25" text-anchor="middle" font-size="14" font-weight="bold" fill="#8b6914">$</text>
    </svg>`
  );

function normalizeTotalUserCount(value) {
  return Number(value) === 2 ? 2 : 4;
}
const TURN_SECONDS = 30;

function formatTurnTime(secondsLeft) {
  const safe = Math.max(0, Number(secondsLeft) || 0);
  return `00:${String(safe).padStart(2, "0")}`;
}

function formatDurationFromSeconds(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function getNextTurnUserId(currentTurnId, activePlayers) {
  if (!Array.isArray(activePlayers) || !activePlayers.length) return "";
  const ids = activePlayers
    .map((p) => String(p?.userId || "").trim())
    .filter(Boolean);
  if (!ids.length) return "";
  const currentIndex = ids.indexOf(String(currentTurnId || "").trim());
  if (currentIndex < 0) return ids[0];
  return ids[(currentIndex + 1) % ids.length];
}

function isRowFullyRemoved(removedBlockKeys, rowIndex) {
  if (!Array.isArray(removedBlockKeys)) return false;
  const keyA = `${rowIndex}-0`;
  const keyB = `${rowIndex}-1`;
  const keyC = `${rowIndex}-2`;
  return (
    removedBlockKeys.includes(keyA) &&
    removedBlockKeys.includes(keyB) &&
    removedBlockKeys.includes(keyC)
  );
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

const LEGACY_JENGA_PENDING_GAMEOVER_POPUP_KEY = "jengaPendingGameoverPopup";

function migrateLegacyPendingGameoverPopupFlagOnce() {
  try {
    sessionStorage.removeItem(LEGACY_JENGA_PENDING_GAMEOVER_POPUP_KEY);
  } catch {
    // ignore storage failures
  }
}

function pendingGameoverPopupStorageKey(sessionId, lobbyName) {
  const sid = String(sessionId || "").trim();
  const lobby = String(lobbyName || "").trim();
  if (!sid || !lobby) return "";
  return `${LEGACY_JENGA_PENDING_GAMEOVER_POPUP_KEY}:${sid}:${lobby}`;
}

function readPendingGameoverPopupFlag(sessionId, lobbyName) {
  try {
    const key = pendingGameoverPopupStorageKey(sessionId, lobbyName);
    if (!key) return false;
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writePendingGameoverPopupFlag(enabled, sessionId, lobbyName) {
  try {
    const key = pendingGameoverPopupStorageKey(sessionId, lobbyName);
    if (!key) return;
    if (enabled) sessionStorage.setItem(key, "1");
    else sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

function getDisplayName(userData) {
  const name = String(userData?.name ?? "").trim();
  if (name && name.toLowerCase() !== "null null") return name;
  const first = String(userData?.firstName ?? "").trim();
  const last = String(userData?.lastName ?? "").trim();
  return `${first} ${last}`.trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-6]$/.test(value) ? value : "";
}

function getPlayerLabel(player, fallbackUserData, order) {
  const fromName = String(player?.name ?? "").trim();
  if (fromName && fromName.toLowerCase() !== "null null") return fromName;

  const first = String(player?.firstName ?? player?.first_name ?? "").trim();
  const last = String(player?.lastName ?? player?.last_name ?? "").trim();
  const fullName = `${first} ${last}`.trim();
  if (fullName && fullName.toLowerCase() !== "null null") return fullName;

  const fromEmail = String(player?.email ?? "").trim();
  if (fromEmail) return fromEmail;

  const fromEmpId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (fromEmpId) return fromEmpId;

  const fallbackName = String(fallbackUserData?.name ?? "").trim();
  if (fallbackName && fallbackName.toLowerCase() !== "null null") return fallbackName;

  const fallbackEmail = String(fallbackUserData?.email ?? "").trim();
  if (fallbackEmail) return fallbackEmail;

  const fallbackEmpId = String(
    fallbackUserData?.emp_id ?? fallbackUserData?.employeeId ?? ""
  ).trim();
  if (fallbackEmpId) return fallbackEmpId;

  return `Player ${order}`;
}

// Block dimensions - 3:1 ratio so 3 blocks = 1 square (realistic Jenga proportions)
const BLOCK_LENGTH = 2.4;
const BLOCK_WIDTH = 0.75;
const BLOCK_HEIGHT = 0.4;
const GAP = 0.06; // Gap between blocks
const ROW_GAP = 0.03; // Small gap between rows
const TOWER_BREAK_REDIRECT_DELAY_MS = 900;

function JengaBlock({
  position,
  rotation,
  color,
  label,
  onClick,
  isFalling,
  fallDelay,
  isRemoved,
  removalKind,
  isVerticalRow,
  canInteract,
}) {
  const meshRef = useRef();
  const groupRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [hidden, setHidden] = useState(false);
  const fallStartTime = useRef(null);
  const removeStartTime = useRef(null);
  const initialPos = useRef([...position]);
  const initialRot = useRef([...rotation]);
  const randomOffset = useRef({
    x: (Math.random() - 0.5) * 8,
    z: (Math.random() - 0.5) * 8,
    rotX: (Math.random() - 0.5) * 3,
    rotZ: (Math.random() - 0.5) * 3,
  });

  const SHAKE_SEC = 0.5;
  const SLIDE_SEC = 0.5;

  useFrame((state) => {
    if (!meshRef.current || !groupRef.current) return;

    if (isRemoved && !hidden) {
      if (!removeStartTime.current) {
        removeStartTime.current = state.clock.elapsedTime;
      }

      const elapsed = state.clock.elapsedTime - removeStartTime.current;
      const doShake = removalKind === "wrong";
      const shakeDur = doShake ? SHAKE_SEC : 0;

      if (doShake && elapsed < shakeDur) {
        const t = elapsed / shakeDur;
        groupRef.current.position.x =
          initialPos.current[0] + Math.sin(elapsed * 50) * 0.08 * (1 - t);
        groupRef.current.position.y = initialPos.current[1];
        groupRef.current.position.z = initialPos.current[2];
        meshRef.current.rotation.z = Math.sin(elapsed * 60) * 0.15 * (1 - t);
        meshRef.current.rotation.x = 0;
        meshRef.current.material.opacity = 1;
        return;
      }

      const slideElapsed = elapsed - shakeDur;
      const slideProgress = Math.min(Math.max(0, slideElapsed) / SLIDE_SEC, 1);
      const fadeProgress = Math.max(0, (slideElapsed - 0.4) / 0.3);

      const slideAmount = slideProgress * 3;
      if (isVerticalRow) {
        groupRef.current.position.z = initialPos.current[2] - slideAmount;
        groupRef.current.position.x = initialPos.current[0];
      } else {
        groupRef.current.position.x = initialPos.current[0] - slideAmount;
        groupRef.current.position.z = initialPos.current[2];
      }
      groupRef.current.position.y = initialPos.current[1];
      meshRef.current.rotation.z = 0;

      meshRef.current.material.opacity = Math.max(0, 1 - fadeProgress);

      if (fadeProgress >= 1) {
        setHidden(true);
      }
      return;
    }

    if (isFalling) {
      if (!fallStartTime.current) {
        fallStartTime.current = state.clock.elapsedTime;
      }

      const elapsed = state.clock.elapsedTime - fallStartTime.current;
      const delayedElapsed = Math.max(0, elapsed - fallDelay * 0.025);
      const progress = Math.min(delayedElapsed / 0.7, 1);
      const eased = progress * progress;

      groupRef.current.position.y = initialPos.current[1] - eased * 15;
      groupRef.current.position.x = initialPos.current[0] + randomOffset.current.x * progress;
      groupRef.current.position.z = initialPos.current[2] + randomOffset.current.z * progress;
      meshRef.current.rotation.x = randomOffset.current.rotX * progress * Math.PI;
      meshRef.current.rotation.z = randomOffset.current.rotZ * progress * Math.PI;
    } else if (!isRemoved) {
      fallStartTime.current = null;
      removeStartTime.current = null;
      groupRef.current.position.set(...initialPos.current);
      meshRef.current.rotation.set(0, 0, 0);
      meshRef.current.material.opacity = 1;
    }
  });

  if (hidden) return null;

  let displayColor = color;
  if (isRemoved && removalKind) {
    displayColor = removalKind === "correct" ? "#4CAF50" : "#F44336";
  }
  if (hovered && canInteract && !isRemoved) {
    displayColor = new THREE.Color(color).multiplyScalar(1.3).getStyle();
  }

  return (
    <group ref={groupRef} position={position} rotation={rotation}>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          if (canInteract && !isRemoved && onClick) onClick();
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          if (canInteract && !isRemoved) {
            setHovered(true);
            document.body.style.cursor = "pointer";
          }
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = "auto";
        }}
      >
        <boxGeometry args={[BLOCK_LENGTH, BLOCK_HEIGHT, BLOCK_WIDTH]} />
        <meshStandardMaterial
          color={displayColor}
          roughness={0.6}
          metalness={0.05}
          transparent={true}
          opacity={1}
        />
        <Edges color="#A0855C" lineWidth={1} threshold={15} />
      </mesh>
      {/* Letter on front end of block (Z+ side, after rotation applied) */}
      <Text
        position={[BLOCK_LENGTH / 2 + 0.01, 0, 0]}
        rotation={[0, Math.PI / 2, 0]}
        fontSize={0.25}
        color="#1a1a1a"
        anchorX="center"
        anchorY="middle"
        font={undefined}
      >
        {label}
      </Text>
      {/* Letter on back end of block (Z- side) */}
      <Text
        position={[-BLOCK_LENGTH / 2 - 0.01, 0, 0]}
        rotation={[0, -Math.PI / 2, 0]}
        fontSize={0.25}
        color="#1a1a1a"
        anchorX="center"
        anchorY="middle"
        font={undefined}
      >
        {label}
      </Text>
    </group>
  );
}

function JengaTower({
  onBlockClick,
  isFalling,
  removedBlocks,
  removedBlocksKind,
  canInteract,
  shakeUntil,
  totalRows,
}) {
  const shakeUntilRef = useRef(0);
  shakeUntilRef.current = shakeUntil || 0;
  const shakeGroupRef = useRef();

  useFrame(() => {
    const g = shakeGroupRef.current;
    if (!g) return;
    const until = Number(shakeUntilRef.current) || 0;
    if (until && Date.now() < until) {
      const t = performance.now();
      g.position.x = Math.sin(t * 0.014) * 0.095;
      g.position.z = Math.cos(t * 0.011) * 0.07;
      g.rotation.y = Math.sin(t * 0.009) * 0.055;
      g.rotation.x = Math.cos(t * 0.016) * 0.025;
    } else {
      g.position.x = 0;
      g.position.z = 0;
      g.rotation.y = 0;
      g.rotation.x = 0;
    }
  });

  const blocks = [];
  const safeTotalRows = Math.max(1, Number(totalRows) || 1);

  for (let row = 0; row < safeTotalRows; row++) {
    const y = row * (BLOCK_HEIGHT + ROW_GAP) + BLOCK_HEIGHT / 2;
    const isVerticalRow = row % 2 === 1;

    for (let i = 0; i < 3; i++) {
      const label = LABELS[i];
      const blockKey = `${row}-${i}`;

      let x, z, rotY;
      const blockSpacing = BLOCK_WIDTH + GAP;

      if (isVerticalRow) {
        x = (i - 1) * blockSpacing;
        z = 0;
        rotY = Math.PI / 2;
      } else {
        x = 0;
        z = (i - 1) * blockSpacing;
        rotY = 0;
      }

      const isBlockRemoved = removedBlocks.includes(blockKey);
      const removalKind = removedBlocksKind?.[blockKey] || null;

      const blockColor = OPTION_COLORS[label];

      blocks.push(
        <JengaBlock
          key={blockKey}
          position={[x, y, z]}
          rotation={[0, rotY, 0]}
          color={blockColor}
          label={label}
          onClick={() => onBlockClick(row, i)}
          isFalling={isFalling}
          fallDelay={safeTotalRows - row}
          isRemoved={isBlockRemoved}
          removalKind={removalKind}
          isVerticalRow={isVerticalRow}
          canInteract={canInteract}
        />
      );
    }
  }

  return (
    <group ref={shakeGroupRef}>
      {blocks}
      {/* Base platform */}
      <mesh position={[0, -0.15, 0]} receiveShadow>
        <boxGeometry args={[3.2, 0.3, 3.2]} />
        <meshStandardMaterial color="#5D4E37" roughness={0.9} />
      </mesh>
    </group>
  );
}

function Scene({
  onBlockClick,
  isFalling,
  removedBlocks,
  removedBlocksKind,
  canInteract,
  shakeUntil,
  totalRows,
}) {
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 10, 5]} intensity={1} />
      <directionalLight position={[-5, 8, -5]} intensity={0.5} />
      <pointLight position={[0, 8, 0]} intensity={0.3} />

      {/* Fit tower to viewport so it fills edge-to-edge (no slab of empty beside it) */}
      <Bounds fit observe clip margin={1.32} maxDuration={0.2}>
        <group position={[0, -0.15, 0]}>
          <JengaTower
            onBlockClick={onBlockClick}
            isFalling={isFalling}
            removedBlocks={removedBlocks}
            removedBlocksKind={removedBlocksKind}
            canInteract={canInteract}
            shakeUntil={shakeUntil}
            totalRows={totalRows}
          />
        </group>
      </Bounds>

      <OrbitControls
        makeDefault
        enablePan={false}
        enableZoom={true}
        enableRotate={true}
        autoRotate={false}
        minDistance={1.75}
        maxDistance={45}
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI / 2.2}
        target={[0, 2.55, 0]}
      />
    </>
  );
}

function JengaConfettiBurst({ burstId }) {
  const bits = useMemo(
    () =>
      Array.from({ length: 44 }, (_, i) => ({
        key: `${burstId}-${i}`,
        left: `${8 + Math.random() * 84}%`,
        drift: (Math.random() - 0.5) * 120,
        rot: `${160 + Math.random() * 500}deg`,
        delay: `${Math.random() * 0.32}s`,
        dur: `${1.75 + Math.random() * 1.35}s`,
        hue: Math.floor(Math.random() * 360),
        size: `${5 + Math.random() * 9}px`,
      })),
    [burstId]
  );

  if (!burstId) return null;

  return (
    <div className="jenga-confetti-layer" aria-hidden>
      {bits.map((b) => (
        <span
          key={b.key}
          className="jenga-confetti-bit"
          style={{
            left: b.left,
            width: b.size,
            height: `calc(${b.size} * 0.5)`,
            background: `hsl(${b.hue} 88% 58%)`,
            animationDelay: b.delay,
            animationDuration: b.dur,
            ["--jc-drift"]: `${b.drift}px`,
            ["--jc-rot"]: b.rot,
          }}
        />
      ))}
    </div>
  );
}

export default function JengaGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const themeColors = themeData?.colors || themeData || {};
  const textStyle = themeColors?.text_color
    ? { color: themeColors.text_color }
    : undefined;
  const rulesButtonStyle =
    themeColors?.option_color != null && themeColors?.option_text_color != null
      ? {
          background: themeColors.option_color,
          color: themeColors.option_text_color,
        }
      : undefined;
  const rulesList = Array.isArray(themeData?.rules)
    ? themeData.rules.filter((rule) => typeof rule === "string" && rule.trim())
    : [];
  const scorePerQuestion = Number.isFinite(Number(themeData?.correctPoints ))
    ? Number(themeData.correctPoints )
    : 10;
    // console.log("scorePerQuestion", scorePerQuestion);
  const gameRules = rulesList.length
    ? rulesList
    : [
        "Click the block that matches the correct answer.",
        "Correct answer gives points.",
        "Wrong answer reduces one life.",
        "Turn timer starts after you press Start.",
      ];
  const userData = useMemo(() => readStoredUserData(), []);

  const [maxLobbyPlayers, setMaxLobbyPlayers] = useState(() => {
    try {
      const stored = sessionStorage.getItem(TOTAL_USER_COUNT_KEY);
      if (stored != null) return normalizeTotalUserCount(stored);
    } catch {
      // ignore storage failures
    }
    return normalizeTotalUserCount(userData?.totalUserCount);
  });

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [towerFalling, setTowerFalling] = useState(false);
  const [gameStatus, setGameStatus] = useState("playing");
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(5);
  const [loading, setLoading] = useState(true);
  const [removedBlocks, setRemovedBlocks] = useState([]);
  const [removedBlocksKind, setRemovedBlocksKind] = useState({});
  const [recentSelection, setRecentSelection] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerStatsMap, setPlayerStatsMap] = useState({});
  const [playersLoading, setPlayersLoading] = useState(true);
  const [turnUserId, setTurnUserId] = useState("");
  const [turnTimeLeft, setTurnTimeLeft] = useState(TURN_SECONDS);
  const [turnStartedAtMs, setTurnStartedAtMs] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(true);
  const [showRulesPopup, setShowRulesPopup] = useState(false);
  const [gameStartAtMs, setGameStartAtMs] = useState(null);
  const [towerShakeUntil, setTowerShakeUntil] = useState(0);
  const [confettiBurstId, setConfettiBurstId] = useState(0);
  const playersRef = useRef([]);
  const turnUserIdRef = useRef("");
  const clearRecentSelectionTimeoutRef = useRef(null);
  const gameoverScoreSyncRef = useRef(false);
  const playerStatsMapRef = useRef({});
  const adminTokenRef = useRef("");
  const [jengaFinalReportSynced, setJengaFinalReportSynced] = useState(false);
  const questionsCompletedRef = useRef(false);
  const gameoverPopupShownRef = useRef(false);
  const pendingGameoverPopupRef = useRef(false);
  const lobbyDocStatusRef = useRef("");
  const [lobbyDocStatus, setLobbyDocStatus] = useState("");

  const userId = String(user?.userId ?? userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = String(userData?.lobby ?? location.state?.lobbyName ?? "").trim();
  const displayName = getDisplayName(userData);
  const lobbyDocRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
  }, [lobbyName, sessionId]);

  useEffect(() => {
    migrateLegacyPendingGameoverPopupFlagOnce();
  }, []);

  useEffect(() => {
    lobbyDocStatusRef.current = "";
    setLobbyDocStatus("");
  }, [sessionId, lobbyName]);

  useEffect(() => {
    pendingGameoverPopupRef.current = false;
    if (!sessionId || !lobbyName) return;
    pendingGameoverPopupRef.current =
      readPendingGameoverPopupFlag(sessionId, lobbyName) ||
      location.state?.fromLeaderboard === true;
  }, [sessionId, lobbyName, location.state?.fromLeaderboard]);

  /** Shared tower + score so every client sees removals and animations */
  const jengaStateDocRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(
      database,
      "lobbies",
      sessionId,
      "lobby",
      lobbyName,
      "state",
      "live"
    );
  }, [lobbyName, sessionId]);

  const myTurn = turnUserId && userId && String(turnUserId) === String(userId);

  const claimFinalReportSync = async () => {
    if (!jengaStateDocRef || !userId) return false;
    try {
      const claimed = await runTransaction(database, async (tx) => {
        const snap = await tx.get(jengaStateDocRef);
        if (!snap.exists()) return false;
        const data = snap.data() || {};
        const alreadyDone = data?.finalReportSynced === true;
        const inProgress = data?.finalReportSyncInProgress === true;
        if (alreadyDone || inProgress) return false;
        tx.set(
          jengaStateDocRef,
          {
            finalReportSyncInProgress: true,
            finalReportSyncStartedBy: userId,
            finalReportSyncStartedAt: serverTimestamp(),
          },
          { merge: true }
        );
        return true;
      });
      return claimed === true;
    } catch (e) {
      console.error("Failed to claim final report sync lock:", e);
      return false;
    }
  };

  const persistJengaState = async ({
    patch,
    nextRemovedBlockKey,
    nextRemovedBlockKind,
    nextTowerFalling,
    nextGameStatus,
    mirrorLobbyGameover = false,
  } = {}) => {
    if (!jengaStateDocRef) return;
    const mergedPatch = patch && typeof patch === "object" ? { ...patch } : {};
    let prevStateData = null;
    const readPrevState = async () => {
      if (prevStateData !== null) return prevStateData;
      const prevSnap = await getDoc(jengaStateDocRef);
      prevStateData = prevSnap.exists() ? prevSnap.data() || {} : {};
      return prevStateData;
    };

    if (nextRemovedBlockKey && nextRemovedBlockKind) {
      try {
        const prevData = await readPrevState();
        const prevKinds =
          prevData?.removedBlocksKind &&
          typeof prevData.removedBlocksKind === "object" &&
          !Array.isArray(prevData.removedBlocksKind)
            ? prevData.removedBlocksKind
            : {};
        mergedPatch.removedBlocksKind = {
          ...prevKinds,
          [nextRemovedBlockKey]: nextRemovedBlockKind,
        };
      } catch (e) {
        console.error("Failed to merge removedBlocksKind:", e);
        mergedPatch.removedBlocksKind = {
          [nextRemovedBlockKey]: nextRemovedBlockKind,
        };
      }
    }

    if (
      mergedPatch.playerStats &&
      typeof mergedPatch.playerStats === "object" &&
      !Array.isArray(mergedPatch.playerStats)
    ) {
      try {
        const prevData = await readPrevState();
        const prevStats =
          prevData?.playerStats &&
          typeof prevData.playerStats === "object" &&
          !Array.isArray(prevData.playerStats)
            ? prevData.playerStats
            : {};
        mergedPatch.playerStats = {
          ...prevStats,
          ...mergedPatch.playerStats,
        };
      } catch (e) {
        console.error("Failed to merge playerStats:", e);
      }
    }

    const payload = {
      ...mergedPatch,
      updatedAt: serverTimestamp(),
      updatedBy: userId || "",
    };
    if (nextTowerFalling !== undefined) payload.towerFalling = nextTowerFalling;
    if (nextGameStatus) payload.status = nextGameStatus;
    if (nextRemovedBlockKey) {
      payload.removedBlocks = arrayUnion(nextRemovedBlockKey);
    }
    try {
      await setDoc(jengaStateDocRef, payload, { merge: true });
      if (mirrorLobbyGameover && lobbyDocRef) {
        await setDoc(lobbyDocRef, { status: "gameover" }, { merge: true });
      }
    } catch (e) {
      console.error("Failed to persist Jenga state:", e);
    }
  };

  /** Ensure baseline doc exists (first player in lobby) */
  useEffect(() => {
    if (!jengaStateDocRef) return undefined;

    let cancelled = false;
    const ensure = async () => {
      try {
        const snap = await getDoc(jengaStateDocRef);
        if (cancelled || snap.exists()) return;
        await setDoc(
          jengaStateDocRef,
          {
            removedBlocks: [],
            removedBlocksKind: {},
            status: "playing",
            gameStartTime: serverTimestamp(),
            lives: MAX_LIVES,
            score: 0,
            towerFalling: false,
            time:0,
            playerStats: {},
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        if (!cancelled) console.error("Failed to initialize Jenga state doc:", e);
      }
    };
    ensure();
    return () => {
      cancelled = true;
    };
  }, [jengaStateDocRef]);

  useEffect(() => {
    if (!jengaStateDocRef) return undefined;
    let isActive = true;

    const unsub = onSnapshot(
      jengaStateDocRef,
      (snap) => {
        if (!isActive) return;
        const d = snap.data() || {};
        if (!snap.exists()) return;

        setJengaFinalReportSynced(d.finalReportSynced === true);

        const rb = d.removedBlocks;
        if (Array.isArray(rb)) {
          setRemovedBlocks(rb);
        }
        const rbk = d.removedBlocksKind;
        if (rbk && typeof rbk === "object" && !Array.isArray(rbk)) {
          setRemovedBlocksKind({ ...rbk });
        }
        const st = String(d.status || "").trim().toLowerCase();
        if (st === "playing") {
          setGameStatus("playing");
        } else if (st === "gameover") {
          if (lobbyDocStatusRef.current === "gameover") {
            setGameStatus("gameover");
          }
        }
        const gameStartTs = d.gameStartTime;
        if (gameStartTs?.toMillis) {
          setGameStartAtMs(gameStartTs.toMillis());
        }
        if (typeof d.lives === "number") {
          const n = Math.max(0, Math.min(5, d.lives));
          setLives(n);
        }
        if (typeof d.score === "number") setScore(Math.max(0, d.score));
        if (typeof d.towerFalling === "boolean") setTowerFalling(d.towerFalling);
        const stats = d.playerStats && typeof d.playerStats === "object" ? d.playerStats : {};
        setPlayerStatsMap(stats);
        const myStats = userId ? stats[userId] : null;
        if (myStats && typeof myStats === "object") {
          const myLives = Number(myStats.lives);
          const myScore = Number(myStats.score);
          if (Number.isFinite(myLives)) setLives(Math.max(0, Math.min(MAX_LIVES, myLives)));
          if (Number.isFinite(myScore)) setScore(Math.max(0, myScore));
        }
      },
      (e) => {
        if (isActive) console.error("Jenga state snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      unsub();
    };
  }, [jengaStateDocRef, userId]);

  useEffect(() => {
    const shouldShowGameoverPopup =
      lobbyDocStatus === "gameover" &&
      (gameStatus === "gameover" || pendingGameoverPopupRef.current);

      console.log("shouldShowGameoverPopup", shouldShowGameoverPopup);
    if (!shouldShowGameoverPopup) {
      gameoverPopupShownRef.current = false;
      return undefined;
    }
    if (gameoverPopupShownRef.current) return undefined;

    // Ensure tower break starts before redirecting to leaderboard.
    if (gameStatus === "gameover" && !towerFalling) {
      setTowerFalling(true);
      return undefined;
    }

    gameoverPopupShownRef.current = true;
    pendingGameoverPopupRef.current = false;
    writePendingGameoverPopupFlag(false, sessionId, lobbyName);

    const redirectDelayMs =
      gameStatus === "gameover" ? TOWER_BREAK_REDIRECT_DELAY_MS : 0;
    const redirectTimer = window.setTimeout(() => {
      navigate("/leaderboard", { state: { fromJengaGameover: true } });
    }, redirectDelayMs);
    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [gameStatus, lobbyDocStatus, lobbyName, navigate, sessionId, towerFalling]);

  useEffect(() => {
    if (gameStatus !== "gameover") {
      gameoverScoreSyncRef.current = false;
      return;
    }
    if (jengaFinalReportSynced) return;
    if (gameoverScoreSyncRef.current) return;
    if (!sessionId || !lobbyName || !userData?.organizationId) return;
    if (!playersRef.current?.length) return;

    gameoverScoreSyncRef.current = true;
    (async () => {
      const releaseLock = async () => {
        if (!jengaStateDocRef) return;
        await setDoc(
          jengaStateDocRef,
          { finalReportSyncInProgress: false },
          { merge: true }
        ).catch(() => {});
      };

      const lockClaimed = await claimFinalReportSync();
      if (!lockClaimed) {
        gameoverScoreSyncRef.current = false;
        return;
      }

      let freshUsers = [];
      try {
        const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
        const usersSnap = await getDocs(collection(database, usersPath));
        freshUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("Failed to refetch users for final score sync:", e);
        freshUsers = playersRef.current || [];
      }

      if (!freshUsers.length) {
        gameoverScoreSyncRef.current = false;
        await releaseLock();
        return;
      }

      const statsByUser = playerStatsMapRef.current || {};
      const adminTok = String(adminTokenRef.current || "");

      const usersPayload = freshUsers
        .map((playerRow, idx) => {
          const uid = String(playerRow?.userId || playerRow?.id || "").trim();
          if (!uid) return null;
          const stats = statsByUser?.[uid] || {};

          const scoreVal = Number.isFinite(Number(playerRow?.score))
            ? Math.max(0, Number(playerRow.score))
            : Number.isFinite(Number(stats?.score))
              ? Math.max(0, Number(stats.score))
              : 0;

          const totalAnswerTimeSecVal = Number.isFinite(Number(playerRow?.totalAnswerTimeSec))
            ? Math.max(0, Math.floor(Number(playerRow.totalAnswerTimeSec)))
            : Number.isFinite(Number(stats?.totalAnswerTimeSec))
              ? Math.max(0, Math.floor(Number(stats.totalAnswerTimeSec)))
              : 0;

          const timeFromDoc = String(playerRow?.time || stats?.time || "").trim();
          const timeVal = timeFromDoc || formatDurationFromSeconds(totalAnswerTimeSecVal);

          const wrongVal = Number.isFinite(Number(playerRow?.wrongAnswers))
            ? Math.max(0, Number(playerRow.wrongAnswers))
            : Number.isFinite(Number(stats?.wrongAnswers))
              ? Math.max(0, Number(stats.wrongAnswers))
              : 0;

          return {
            userId: uid,
            sessionId,
            organizationId: userData.organizationId,
            name:
              String(playerRow?.name || stats?.name || "").trim() ||
              getPlayerLabel(playerRow, uid === userId ? userData : null, idx + 1),
            score: scoreVal,
            wrongCount: wrongVal,
            status: "gameover",
            totalTime: timeVal,
            time: timeVal,
            totalAnswerTimeSec: totalAnswerTimeSecVal,
            role: String(userData?.role || ""),
            token: String(userData?.token || adminTok || ""),
            gameId: String(userData?.gameId || ""),
            reportId: playerRow?.reportId || stats?.reportId || null,
          };
        })
        .filter((u) => Boolean(u?.userId));

      if (!usersPayload.length) {
        gameoverScoreSyncRef.current = false;
        await releaseLock();
        return;
      }

      try {
        await updateScore({
          sessionId,
          organizationId: userData.organizationId,
          users: usersPayload,
          role: String(userData?.role || ""),
          token: String(userData?.token || adminTok || ""),
          gameId: String(userData?.gameId || ""),
        });
      } catch (e) {
        gameoverScoreSyncRef.current = false;
        await releaseLock();
        console.error("Failed to sync final score report:", e);
        return;
      }

      await Promise.allSettled(
        usersPayload.map(async (user) => {
          const reportData = {
            sessionId: user.sessionId || sessionId,
            role: user.role || String(userData?.role || ""),
            token: user.token || userData?.token || adminTok || null,
            name: user.name,
            userId: user.userId,
            gameId: user.gameId || String(userData?.gameId || ""),
            organizationId: user.organizationId || userData.organizationId,
            points: user.score,
            time: user.time ?? user.totalTime,
            reportId: user.reportId || null,
            ans: "",
          };
          try {
            await sendReport(reportData);
          } catch (reportErr) {
            console.warn("Report request failed:", reportErr);
          }
        })
      );

      if (jengaStateDocRef) {
        await setDoc(
          jengaStateDocRef,
          {
            finalReportSyncInProgress: false,
            finalReportSynced: true,
            finalReportSyncedBy: userId,
            finalReportSyncedAt: serverTimestamp(),
          },
          { merge: true }
        ).catch((e) => {
          console.error("Failed to mark final report sync done:", e);
        });
      }
    })();
  }, [
    gameStatus,
    jengaFinalReportSynced,
    jengaStateDocRef,
    lobbyName,
    players.length,
    sessionId,
    userData?.organizationId,
    userId,
  ]);

  useEffect(() => {
    return () => {
      if (clearRecentSelectionTimeoutRef.current) {
        window.clearTimeout(clearRecentSelectionTimeoutRef.current);
        clearRecentSelectionTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    // html.style.overflow = "hidden";
    // body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    playerStatsMapRef.current = playerStatsMap;
  }, [playerStatsMap]);

  useEffect(() => {
    adminTokenRef.current = adminToken;
  }, [adminToken]);

  useEffect(() => {
    turnUserIdRef.current = turnUserId;
  }, [turnUserId]);

  useEffect(() => {
    if (!rulesAccepted || !turnUserId || !turnStartedAtMs) {
      setTurnTimeLeft(TURN_SECONDS);
      return undefined;
    }

    const tick = () => {
      const elapsed = Math.floor((Date.now() - turnStartedAtMs) / 1000);
      const left = Math.max(0, TURN_SECONDS - elapsed);
      setTurnTimeLeft(left);
    };
    tick();
    const timerId = window.setInterval(tick, 250);
    return () => window.clearInterval(timerId);
  }, [rulesAccepted, turnUserId, turnStartedAtMs, currentIndex]);

  useEffect(() => {
    if (!jengaStateDocRef || !userId) return;

    let cancelled = false;
    const playerFromList = players.find((p) => String(p?.userId || "").trim() === String(userId));

    const ensureMyStats = async () => {
      try {
        const snap = await getDoc(jengaStateDocRef);
        if (cancelled) return;
        const data = snap.exists() ? snap.data() || {} : {};
        const existing = data?.playerStats?.[userId];
        const hasExisting =
          existing &&
          typeof existing === "object" &&
          !Array.isArray(existing) &&
          Object.keys(existing).length > 0;
        if (hasExisting) return;

        await persistJengaState({
          patch: {
            playerStats: {
              [userId]: {
                name: displayName || getPlayerLabel(playerFromList || {}, userData, 1),
                score: 0,
                lives: MAX_LIVES,
                wrongAnswers: 0,
                totalAnswerTimeSec: 0,
                lastAnswerTimeSec: 0,
                character: normalizeCharacterName(playerFromList?.character) || "",
                updatedAt: Timestamp.now(),
              },
            },
          },
        });
      } catch (e) {
        if (!cancelled) console.error("Failed to ensure player stats:", e);
      }
    };

    ensureMyStats();
    return () => {
      cancelled = true;
    };
  }, [displayName, jengaStateDocRef, playerStatsMap, players, userData, userId]);

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) {
      setPlayersLoading(false);
      return;
    }

    let unsubscribe = null;
    let isActive = true;

    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const usersRef = collection(database, usersPath);
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    const nextCharacter = "c" + String(Math.floor(Math.random() * 6) + 1);

    const joinCurrentUser = async () => {
      try {
        if (!isActive) return;
        const existingUserDoc = await getDoc(userDocRef);

        if (!existingUserDoc.exists()) {
          await setDoc(
            userDocRef,
            {
              userId,
              sessionId,
              lobby: lobbyName,
              organizationId: userData?.organizationId || "",
              name: displayName,
              email: userData?.email || "",
              emp_id: userData?.emp_id || userData?.employeeId || "",
              employeeId: userData?.employeeId || userData?.emp_id || "",
              character: nextCharacter,
              timestamp: Date.now(),
              status: "live",
              wrongAnswers: 0,
              lives: MAX_LIVES,
              score: 0,
              lastAnswerTimeSec: 0,
              totalAnswerTimeSec: 0,
              time: formatDurationFromSeconds(0),
            },
            { merge: true }
          );
        } else {
          // Keep existing fields (especially `character`), but ensure they're marked live.
          await setDoc(userDocRef, { status: "live" }, { merge: true });
        }
      } catch (e) {
        if (isActive) console.error("Failed to upsert current lobby user:", e);
      }
    };

    setPlayersLoading(true);
    joinCurrentUser();

    unsubscribe = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!isActive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Show all lobby users in cards so avatars don't disappear when someone goes offline.
        setPlayers(list.slice(0, maxLobbyPlayers));
        setPlayersLoading(false);
      },
      (e) => {
        if (!isActive) return;
        console.error("Jenga live users snapshot failed:", e);
        setPlayers([]);
        setPlayersLoading(false);
      }
    );

    return () => {
      isActive = false;
      if (unsubscribe) unsubscribe();
    };
  }, [displayName, lobbyName, maxLobbyPlayers, sessionId, userData, userId]);

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) return undefined;

    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    let markedOffline = false;

    const markOfflineAndSkipTurn = async () => {
      if (markedOffline) return;
      markedOffline = true;

      try {
        await setDoc(userDocRef, { status: "offline" }, { merge: true });

        if (!lobbyDocRef) return;
        const currentTurn = String(turnUserIdRef.current || "").trim();
        if (currentTurn !== String(userId)) return;

        const activePlayersWithoutMe = (playersRef.current || []).filter(
          (p) => p?.status === "live" && String(p?.userId || "").trim() !== String(userId)
        );
        const nextTurn = getNextTurnUserId(currentTurn, activePlayersWithoutMe);
        await setDoc(
          lobbyDocRef,
          { turn: nextTurn || "", turnStartedAt: Date.now() },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to mark offline/skip turn:", e);
      }
    };

    const handleBeforeUnload = () => {
      markOfflineAndSkipTurn();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [lobbyDocRef, lobbyName, sessionId, userId]);

  useEffect(() => {
    if (!lobbyDocRef) return undefined;
    let isActive = true;

    const unsubscribe = onSnapshot(
      lobbyDocRef,
      (snap) => {
        if (!isActive || !snap.exists()) return;
        const liveData = snap.data() || {};
        const liveTurn = String(liveData?.turn ?? "").trim();
        const liveCurrentQuestion = Number(liveData?.currentQuestion);
        const liveTurnStartedAt = Number(liveData?.turnStartedAt);
        const liveRulesAcceptedAt = Number(liveData?.rulesAcceptedAt);
        const liveRulesAccepted =
          liveData?.rulesAccepted === true ||
          (Number.isFinite(liveRulesAcceptedAt) && liveRulesAcceptedAt > 0);
        if (liveRulesAccepted) {
          setRulesAccepted(true);
        }
        setTurnUserId(liveTurn);
        if (Number.isFinite(liveTurnStartedAt) && liveTurnStartedAt > 0) {
          setTurnStartedAtMs(liveTurnStartedAt);
        } else if (liveTurn && (rulesAccepted || liveRulesAccepted)) {
          setTurnStartedAtMs(Date.now());
          setDoc(lobbyDocRef, { turnStartedAt: Date.now() }, { merge: true }).catch(() => {});
        }

        if (Number.isFinite(liveCurrentQuestion) && liveCurrentQuestion >= 0) {
          setCurrentIndex((prev) => (prev === liveCurrentQuestion ? prev : liveCurrentQuestion));
        }
        const normalizedLobbyStatus = String(liveData?.status ?? "").trim().toLowerCase();
        lobbyDocStatusRef.current = normalizedLobbyStatus;
        setLobbyDocStatus(normalizedLobbyStatus);

        if (normalizedLobbyStatus === "gameover") {
          setGameStatus("gameover");
        }

        if (normalizedLobbyStatus === "play") {
          pendingGameoverPopupRef.current = false;
          gameoverPopupShownRef.current = false;
          writePendingGameoverPopupFlag(false, sessionId, lobbyName);
        }

        const resolvedTotalUserCount = normalizeTotalUserCount(liveData?.totalUserCount);
        setMaxLobbyPlayers(resolvedTotalUserCount);
        try {
          sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(resolvedTotalUserCount));
        } catch {
          // ignore storage write failures
        }
      },
      (e) => {
        if (!isActive) return;
        console.error("Jenga turn snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [lobbyDocRef, rulesAccepted]);

  useEffect(() => {
    if (!lobbyDocRef || playersLoading || !players.length) return;

    const activePlayers = players.filter((p) => p?.status === "live");
    const activeIds = activePlayers
      .map((p) => String(p?.userId || "").trim())
      .filter(Boolean);
    if (!activeIds.length) return;

    const currentTurn = String(turnUserId || "").trim();
    const hasValidTurn = currentTurn && activeIds.includes(currentTurn);
    if (hasValidTurn) return;

    const nextTurn = activeIds[0];
    setDoc(lobbyDocRef, { turn: nextTurn, turnStartedAt: Date.now() }, { merge: true }).catch((e) => {
      console.error("Failed to initialize/fix turn:", e);
    });
  }, [lobbyDocRef, players, playersLoading, turnUserId]);

  useEffect(() => {
    if (!rulesAccepted) return undefined;
    if (!lobbyDocRef || !turnUserId || !turnStartedAtMs) return undefined;
    if (gameStatus === "gameover" || towerFalling) return undefined;

    const msLeft = turnStartedAtMs + TURN_SECONDS * 1000 - Date.now();
    const runTurnTimeout = async () => {
      try {
        const snap = await getDoc(lobbyDocRef);
        if (!snap.exists()) return;
        const lobbyData = snap.data() || {};
        const liveTurn = String(lobbyData?.turn || "").trim();
        const liveStatus = String(lobbyData?.status || "").trim();
        if (liveStatus === "gameover") return;
        if (liveTurn !== String(turnUserId)) return;
        const activePlayers = players.filter((p) => p?.status === "live");
        const nextTurn = getNextTurnUserId(liveTurn, activePlayers);
        await setDoc(
          lobbyDocRef,
          { turn: nextTurn || "", turnStartedAt: Date.now() },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to skip timed-out turn:", e);
      }
    };

    if (msLeft <= 0) {
      runTurnTimeout();
      return undefined;
    }

    const timeoutId = window.setTimeout(runTurnTimeout, msLeft + 50);
    return () => window.clearTimeout(timeoutId);
  }, [gameStatus, lobbyDocRef, players, rulesAccepted, towerFalling, turnStartedAtMs, turnUserId]);

  useEffect(() => {
    if (loading || !questions.length) return;

    const allQuestionsCompleted = currentIndex >= questions.length;
    if (!allQuestionsCompleted) {
      questionsCompletedRef.current = false;
      return;
    }
    if (questionsCompletedRef.current) return;
    questionsCompletedRef.current = true;

    setTowerFalling(true);
    setGameStatus("gameover");

    markUsersGameover();
    persistJengaState({
      nextTowerFalling: true,
      nextGameStatus: "gameover",
      mirrorLobbyGameover: true,
      patch: {
        status: "gameover",
        gameEndTime: serverTimestamp(),
        totalGameTimeMs:
          Number.isFinite(gameStartAtMs) && gameStartAtMs > 0
            ? Math.max(0, Date.now() - gameStartAtMs)
            : null,
      },
    });
  }, [currentIndex, gameStartAtMs, loading, questions.length]);

  const slots = useMemo(() => {
    const liveCardColors = ["#2f99e9", "#6f19d8", "#12cd38", "#eb0f4b"];
    const roster = players.slice(0, maxLobbyPlayers);
    const turnId = String(turnUserId || "").trim();

    const filled = roster.map((p, idx) => ({
      userId: String(p?.userId || "").trim(),
      joined: true,
      label: getPlayerLabel(p, String(p?.userId || "") === userId ? userData : null, idx + 1),
      order: idx + 1,
      isMe: String(p?.userId || "") === userId,
      character: normalizeCharacterName(p?.character),
      isLive: String(p?.status || "").trim().toLowerCase() === "live",
      bg: liveCardColors[idx] || undefined,
      score: Number(playerStatsMap?.[String(p?.userId || "").trim()]?.score || 0),
      lives: Math.max(
        0,
        Math.min(
          MAX_LIVES,
          Number.isFinite(Number(playerStatsMap?.[String(p?.userId || "").trim()]?.lives))
            ? Number(playerStatsMap?.[String(p?.userId || "").trim()]?.lives)
            : MAX_LIVES
        )
      ),
      wrongAnswers: Math.max(
        0,
        Number.isFinite(Number(playerStatsMap?.[String(p?.userId || "").trim()]?.wrongAnswers))
          ? Number(playerStatsMap?.[String(p?.userId || "").trim()]?.wrongAnswers)
          : 0
      ),
      isCurrentTurn:
        Boolean(turnId) &&
        String(p?.userId || "").trim() === turnId &&
        String(p?.status || "").trim().toLowerCase() === "live",
    }));

    while (filled.length < maxLobbyPlayers) {
      const idx = filled.length;
      filled.push({
        joined: false,
        label: "......",
        order: idx + 1,
        isMe: false,
        character: "",
        isLive: false,
        bg: undefined,
        score: 0,
        lives: MAX_LIVES,
        wrongAnswers: 0,
      });
    }
    return filled;
  }, [maxLobbyPlayers, playerStatsMap, players, turnUserId, userData, userId]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetchUserQuestions({
      userId: user.userId,
      token: adminToken,
    })
      .then((data) => {
        const qs = data.allQuestions || [];
        const mapped = qs.map((q) => {
          let opts = [];
          if (Array.isArray(q.options) && q.options.length) {
            opts = q.options.slice(0, 3);
            while (opts.length < 3) opts.push("");
          } else {
            opts = [
              q.option_one || q.option1 || "",
              q.option_two || q.option2 || "",
              q.option_three || q.option3 || "",
            ];
          }

          return {
            id: q.id,
            question: q.question || q.question_name || "",
            options: opts.filter(Boolean),
            answer_id: q.answer_id,
          };
        });

        setQuestions(mapped);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        Swal.fire({
          title: "Error loading questions!",
          text: err.message,
          icon: "error",
        });
        setLoading(false);
      });
  }, [user, adminToken]);

  const currentQ = questions[currentIndex];
  const totalQuestions = questions.length;

  const buildUpdatedFirestoreQuestions = (existingQuestions, completedIndex, nextIndex) => {
    if (!Array.isArray(existingQuestions) || !existingQuestions.length) return undefined;

    return existingQuestions.map((q, idx) => {
      if (!q || typeof q !== "object" || Array.isArray(q)) return q;

      const isDone = idx <= completedIndex;
      const isCurrent = idx === nextIndex;
      const status = isDone ? "done" : isCurrent ? "current" : "pending";

      return {
        ...q,
        isDone,
        isCurrent,
        status,
      };
    });
  };

  const updateLobbyQuestionProgress = async ({ completedIndex, nextIndex, nextTurnId }) => {
    if (!lobbyDocRef) return;

    try {
      const lobbySnap = await getDoc(lobbyDocRef);
      const lobbyData = lobbySnap.exists() ? lobbySnap.data() : {};
      const existingQuestions = Array.isArray(lobbyData?.questions) ? lobbyData.questions : [];
      const updatedQuestions = buildUpdatedFirestoreQuestions(
        existingQuestions,
        completedIndex,
        nextIndex
      );

      const updatePayload = {
        currentQuestion: nextIndex,
        turnStartedAt: Date.now(),
      };

      if (nextTurnId) {
        updatePayload.turn = nextTurnId;
      }

      if (updatedQuestions) {
        updatePayload.questions = updatedQuestions;
      }

      await setDoc(lobbyDocRef, updatePayload, { merge: true });
    } catch (e) {
      console.error("Failed to update question progress:", e);
    }
  };

  const markUsersGameover = async () => {
    if (!sessionId || !lobbyName) return;
    try {
      const userIds = players
        .map((p) => String(p?.userId || "").trim())
        .filter(Boolean);
      if (!userIds.length) return;
      await Promise.allSettled(
        userIds.map((uid) =>
          setDoc(
            doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${uid}`),
            { status: "gameover", gameoverAt: Date.now() },
            { merge: true }
          )
        )
      );
    } catch (e) {
      console.error("Failed to mark users as gameover:", e);
    }
  };

  const handleTowerBlockClick = async (rowIdx, optionIndex) => {
    if (!rulesAccepted || !myTurn || towerFalling || gameStatus === "gameover") return;
    if (!lobbyDocRef || !currentQ || !sessionId || !lobbyName || !userId) return;

    const blockKey = `${rowIdx}-${optionIndex}`;
    if (removedBlocks.includes(blockKey)) return;

    const correctAnswer = Number(currentQ.answer_id);
    const isAnswerCorrect = optionIndex + 1 === correctAnswer;

    const scoreBefore = score;
    const livesBefore = lives;
    const indexBefore = currentIndex;
    const turnBefore = turnUserId;
    const totalQ = questions.length;

    const existingMyStats = playerStatsMap?.[userId] || {};
    const playerFromList = players.find((p) => String(p?.userId || "").trim() === String(userId));

    const wrongRaw = existingMyStats?.wrongAnswers;
    const wrongBefore = Number.isFinite(Number(wrongRaw))
      ? Math.max(0, Number(wrongRaw))
      : Math.max(0, MAX_LIVES - livesBefore);
    const wrongAfter = isAnswerCorrect ? wrongBefore : wrongBefore + 1;
    const newLives = Math.max(0, MAX_LIVES - wrongAfter);
    const nextScore = isAnswerCorrect ? scoreBefore + scorePerQuestion : scoreBefore;
    const answerTimeSec = Number.isFinite(Number(turnStartedAtMs))
      ? Math.max(0, Math.floor((Date.now() - Number(turnStartedAtMs)) / 1000))
      : 0;
    // Merge from both sources to avoid resetting if one snapshot is stale.
    const prevTotalFromStats = Number.isFinite(Number(existingMyStats?.totalAnswerTimeSec))
      ? Math.max(0, Number(existingMyStats.totalAnswerTimeSec))
      : 0;
    const prevTotalFromUserDoc = Number.isFinite(Number(playerFromList?.totalAnswerTimeSec))
      ? Math.max(0, Number(playerFromList.totalAnswerTimeSec))
      : 0;
    const prevTotalAnswerTimeSec = Math.max(prevTotalFromStats, prevTotalFromUserDoc);
    const mergedTotalAnswerTimeSec = prevTotalAnswerTimeSec + answerTimeSec;
    const mergedTotalAnswerTime = formatDurationFromSeconds(mergedTotalAnswerTimeSec);

    const updatedMyStats = {
      ...existingMyStats,
      name: displayName || existingMyStats?.name || "",
      character:
        normalizeCharacterName(existingMyStats?.character) ||
        normalizeCharacterName(playerFromList?.character) ||
        "",
      score: nextScore,
      lives: newLives,
      wrongAnswers: wrongAfter,
      lastAnswerTimeSec: answerTimeSec,
      totalAnswerTimeSec: mergedTotalAnswerTimeSec,
      time: mergedTotalAnswerTime,
      updatedAt: Timestamp.now(),
    };
    const lobbyUserDocRef = doc(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/users/${userId}`
    );
    const userDocWritePromise = setDoc(
      lobbyUserDocRef,
      {
        wrongAnswers: wrongAfter,
        lives: newLives,
        score: nextScore,
        lastAnswerTimeSec: answerTimeSec,
        totalAnswerTimeSec: mergedTotalAnswerTimeSec,
        time: mergedTotalAnswerTime,
        statsUpdatedAt: Date.now(),
      },
      { merge: true }
    ).catch((e) => {
      console.error("Failed to persist user stats in users doc:", e);
    });

    const mergedStats = { ...playerStatsMap, [userId]: updatedMyStats };
    const anyPlayerOut = Object.values(mergedStats).some(
      (p) => Number(p?.wrongAnswers ?? 0) >= MAX_LIVES
    );

    const removalKind = isAnswerCorrect ? "correct" : "wrong";
    setRecentSelection({ optionIndex, correct: isAnswerCorrect });
    if (clearRecentSelectionTimeoutRef.current) {
      window.clearTimeout(clearRecentSelectionTimeoutRef.current);
    }
    clearRecentSelectionTimeoutRef.current = window.setTimeout(() => {
      clearRecentSelectionTimeoutRef.current = null;
      setRecentSelection(null);
    }, 1400);
    const nextKindMap = { ...removedBlocksKind, [blockKey]: removalKind };
    const nextRemovedBlocks = removedBlocks.includes(blockKey)
      ? removedBlocks
      : [...removedBlocks, blockKey];
    const rowCollapsed = isRowFullyRemoved(nextRemovedBlocks, rowIdx);
    setRemovedBlocks(nextRemovedBlocks);
    setRemovedBlocksKind(nextKindMap);
    if (isAnswerCorrect) {
      setScore(nextScore);
      setConfettiBurstId((n) => n + 1);
    } else {
      setLives(newLives);
      setTowerShakeUntil(Date.now() + 750);
    }

    if (anyPlayerOut || rowCollapsed) {
      await userDocWritePromise;
      setTowerFalling(true);
      setGameStatus("gameover");
      await markUsersGameover();
      await persistJengaState({
        nextRemovedBlockKey: blockKey,
        nextRemovedBlockKind: removalKind,
        nextTowerFalling: true,
        nextGameStatus: "gameover",
        mirrorLobbyGameover: true,
        patch: {
          status: "gameover",
          gameoverReason: rowCollapsed ? "row_cleared" : "player_out",
          gameEndTime: serverTimestamp(),
          totalGameTimeMs:
            Number.isFinite(gameStartAtMs) && gameStartAtMs > 0
              ? Math.max(0, Date.now() - gameStartAtMs)
              : null,
          playerStats: {
            [userId]: updatedMyStats,
          },
        },
      });
      return;
    }

    await persistJengaState({
      nextRemovedBlockKey: blockKey,
      nextRemovedBlockKind: removalKind,
      patch: {
        playerStats: {
          [userId]: updatedMyStats,
        },
      },
    });

    const nextTurn = getNextTurnUserId(
      turnBefore,
      players.filter((p) => p?.status === "live")
    );
    if (nextTurn) {
      await setDoc(
        lobbyDocRef,
        { turn: nextTurn, turnStartedAt: Date.now() },
        { merge: true }
      );
    }

    window.setTimeout(async () => {
      const completedQuestionIndex = indexBefore;
      const nextQuestionIndex = indexBefore + 1;

      if (nextQuestionIndex >= totalQ) {
        await userDocWritePromise;
        await updateLobbyQuestionProgress({
          completedIndex: completedQuestionIndex,
          nextIndex: totalQ,
          nextTurnId: "",
        });
        setTowerFalling(true);
        setGameStatus("gameover");
        await markUsersGameover();
        await persistJengaState({
          nextTowerFalling: true,
          nextGameStatus: "gameover",
          mirrorLobbyGameover: true,
          patch: {
            status: "gameover",
            gameEndTime: serverTimestamp(),
            totalGameTimeMs:
              Number.isFinite(gameStartAtMs) && gameStartAtMs > 0
                ? Math.max(0, Date.now() - gameStartAtMs)
                : null,
          },
        });
        return;
      }

      setCurrentIndex(nextQuestionIndex);
      await updateLobbyQuestionProgress({
        completedIndex: completedQuestionIndex,
        nextIndex: nextQuestionIndex,
        nextTurnId: "",
      });
    }, 1500);
  };

  const handleRulesStart = async () => {
    setRulesAccepted(true);
    setShowRulesPopup(false);
    if (!lobbyDocRef) return;
    try {
      const snap = await getDoc(lobbyDocRef);
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const liveTurn = String(data?.turn ?? "").trim();
      const liveTurnStartedAt = Number(data?.turnStartedAt);
      const now = Date.now();
      const shouldStartTimer =
        !Number.isFinite(liveTurnStartedAt) ||
        liveTurnStartedAt <= 0 ||
        String(liveTurn) === String(userId);
      const updatePayload = {
        rulesAccepted: true,
        rulesAcceptedAt: now,
      };
      if (shouldStartTimer) {
        setTurnStartedAtMs(now);
        updatePayload.turnStartedAt = now;
      }
      await setDoc(lobbyDocRef, updatePayload, { merge: true });
    } catch (e) {
      console.error("Failed to start turn timer after rules popup:", e);
    }
  };

  useEffect(() => {
    if (gameStatus !== "gameover" || !sessionId || !lobbyName || !userId) return undefined;
    setDoc(
      doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${userId}`),
      { status: "gameover", gameoverAt: Date.now() },
      { merge: true }
    ).catch((e) => {
      console.error("Failed to sync current user gameover status:", e);
    });
    return undefined;
  }, [gameStatus, lobbyName, sessionId, userId]);

  if (loading) {
    return (
      <div className="jenga-loader-container">
        <div className="jenga-loader"></div>
        <p style={textStyle}>Loading Jenga Game...</p>
      </div>
    );
  }

  if (!currentQ && gameStatus !== "gameover") {
    return (
      <div className="jenga-loader-container">
        <p style={textStyle}>No questions available</p>
      </div>
    );
  }

  return (
    <div className="jenga-container">
      <JengaConfettiBurst burstId={confettiBurstId} />
      {showRulesPopup ? (
        <div className="jenga-rules-overlay" role="dialog" aria-modal="true" aria-label="Game rules">
          <div className="jenga-rules-modal">
            <h3 style={textStyle}>Game Rules</h3>
            <ul style={textStyle}>
              {gameRules.map((rule, idx) => (
                <li key={`${idx}-${rule}`}>{rule}</li>
              ))}
            </ul>
            <button
              type="button"
              className="jenga-rules-close-btn"
              onClick={() => setShowRulesPopup(false)}
              style={rulesButtonStyle}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
      <div className="jenga-header">
        <div className="jenga-turn-badge" style={textStyle}>
          {myTurn ? "Your Turn" : "Waiting for turn"}
        </div>
        <div className="jenga-progress">
          <i className="fa-solid fa-list-ol" aria-hidden />
          <span style={textStyle}>{currentIndex + 1}/{totalQuestions}</span>
        </div>
        <div className="jenga-timer">
          <img className="jenga-timer-icon" src="/img/times-up.png" alt="Timer" />
          <span style={textStyle}>{formatTurnTime(turnTimeLeft)}</span>
        </div>
        <button
          type="button"
          className="jenga-rules-help-btn"
          onClick={() => setShowRulesPopup(true)}
          aria-label="Open game rules"
          title="Game Rules"
        >
          ?
        </button>
        <div className="jenga-score">
          <i className="fa-solid fa-trophy"></i>
          <span style={textStyle}>{score}</span>
        </div>
      </div>

      <div className="jenga-main-3d">
        <div
          className="jenga-canvas-container"
          style={
            !myTurn || gameStatus === "gameover" ? { pointerEvents: "none" } : undefined
          }
        >
          <Canvas
            camera={{ position: [6, 6, 8], fov: 48 }}
            gl={{ antialias: true, alpha: true }}
            onCreated={({ scene, gl }) => {
              scene.background = null;
              gl.setClearColor(0x000000, 0);
            }}
          >
              <Suspense fallback={null}>
              <Scene
                onBlockClick={handleTowerBlockClick}
                isFalling={towerFalling}
                removedBlocks={removedBlocks}
                removedBlocksKind={removedBlocksKind}
                canInteract={myTurn}
                shakeUntil={towerShakeUntil}
                totalRows={totalQuestions}
              />
            </Suspense>
          </Canvas>
        </div>

        <div
          className="jenga-question-section"
          style={!myTurn ? { opacity: 0.85 } : undefined}
        >
          <h2 className="jenga-question" style={textStyle}>
            {currentQ?.question || (gameStatus === "gameover" ? "Game Over" : "Loading question...")}
          </h2>

          <div className="jenga-options-grid" style={{ pointerEvents: "none" }}>
            {(currentQ?.options || []).slice(0, 3).map((option, idx) => {
              if (!option) return null;
              const label = LABELS[idx] || String.fromCharCode(65 + idx);
              let optionClass = "jenga-option-card";
              const correctOptionIndex = Number(currentQ?.answer_id) - 1;
              const hasRecentSelection = recentSelection && recentSelection.optionIndex !== null;
              if (hasRecentSelection && idx === correctOptionIndex) {
                optionClass += " selected correct";
              } else if (recentSelection?.optionIndex === idx) {
                optionClass += recentSelection.correct ? " selected correct" : " selected wrong";
              }

              return (
                <div key={idx} className={optionClass}>
                  <span
                    className="jenga-option-letter"
                    style={{ background: OPTION_COLORS[label] || "#888" }}
                  >
                    {label}
                  </span>
                  <span className="jenga-option-text" style={textStyle}>
                    {option}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="jenga-players-bar" aria-live="polite">
        {playersLoading ? (
          <div className="jenga-players-loading" style={textStyle}>
            Loading players...
          </div>
        ) : null}
        <div
          className={`jenga-players-grid jenga-players-grid--${maxLobbyPlayers === 2 ? "two" : "four"}`}
        >
          {slots.map((slot, idx) => (
            <div
              key={`${slot.order}-${idx}`}
              className={`jenga-player-card ${slot.joined ? "joined" : "empty"} ${slot.isMe ? "me" : ""} ${
                slot.joined && slot.isCurrentTurn && gameStatus === "playing" ? "turn-active" : ""
              }`}
              style={
                slot.joined && slot.isLive
                  ? { "--card-accent": slot.bg }
                  : undefined
              }
            >
              <div className="jenga-player-avatar">
                {slot.joined && slot.character ? (
                  <img
                    src={`/img/${slot.character}.png`}
                    alt={slot.character}
                  />
                ) : (
                  <span className="jenga-player-avatar-placeholder">
                    {slot.joined ? "🙂" : "?"}
                  </span>
                )}
              </div>
              <div className="jenga-player-info">
                <div className="jenga-player-top-row">
                  <span className="jenga-player-name" style={textStyle}>
                    {slot.label}
                  </span>
                  {/* <span className="jenga-player-score">{slot.score}</span> */}
                </div>
                {slot.joined && (
                  <div className="jenga-player-bottom-row">
                    <span className="jenga-player-delta">
                      <img src={COIN_IMG_SRC} alt="" className="jenga-coin-icon" width={18} height={18} />
                      <span className="jenga-player-score-value">
                        {slot.score > 0 ? slot.score : 0}
                      </span>
                    </span>
                    <div className="jenga-player-hearts" aria-label={`${slot.lives} lives remaining`}>
                      {Array.from({ length: MAX_LIVES }).map((_, hi) => {
                        const isFull = hi < Number(slot.lives);
                        return (
                          <i
                            key={hi}
                            className={`fa-solid fa-heart jenga-player-heart ${isFull ? "full" : "lost"}`}
                            aria-hidden
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
